import React, { useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { Spin, Table, message } from "antd";
import { Spinner } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { IoCopyOutline } from "react-icons/io5";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import SidebarNav from "../../sidebar";
import CustomPagination from "../../Pagination/pagination";
import FilterPanel from "../../commoncomponent/FilterPanel";
import TableToolbar from "../../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../../hooks/useTableSettings";
import { triggerDownload } from "../../../../config/downloadUtils";
import { getUserWalletTrx } from "../../../../service/api";

const shortenAddress = (addr) => {
  if (!addr) return "-";
  return addr.length > 10 ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : addr;
};

const copyToClipboard = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
  message.success("Copied to clipboard!");
};

const ALL_COLUMNS = [
  {
    key: "transactionId",
    title: "Transaction ID",
    defaultVisible: true,
    render: (r) => (
      <div className="d-flex align-items-center gap-2">
        <span>{shortenAddress(r?.transactionId)}</span>
        {r?.transactionId && (
          <IoCopyOutline style={{ cursor: "pointer" }} onClick={() => copyToClipboard(r.transactionId)} />
        )}
      </div>
    ),
  },
  {
    key: "coin",
    title: "Coin",
    defaultVisible: true,
    render: (r) => (
      <div className="d-flex align-items-center gap-2">
        {r?.coinDetails?.image && (
          <img
            src={r.coinDetails.image}
            alt={r.coinDetails.symbol}
            style={{ width: 24, height: 24, borderRadius: "50%" }}
          />
        )}
        <div>
          <div>{r?.coinDetails?.symbol || "-"}</div>
          <small style={{ color: "#6c757d" }}>{r?.coinDetails?.name}</small>
        </div>
      </div>
    ),
  },
  {
    key: "amount",
    title: "Amount",
    defaultVisible: true,
    render: (r) => (
      <span style={{ fontWeight: 500 }}>
        {r?.amount} {r?.coinDetails?.symbol || ""}
      </span>
    ),
  },
  {
    key: "type",
    title: "Type / SubType",
    defaultVisible: true,
    render: (r) => (
      <div>
        <span
          className={`badge ${r?.type === "CREDIT" ? "bg-success" : "bg-danger"}`}
          style={{ fontSize: 11 }}
        >
          {r?.type}
        </span>
        <br />
        <small style={{ color: "#6c757d" }}>{r?.subType}</small>
      </div>
    ),
  },
  {
    key: "trxStatus",
    title: "Status",
    defaultVisible: true,
    render: (r) => (
      <span
        className={`badge ${
          r?.trxStatus === "COMPLETED"
            ? "bg-success"
            : r?.trxStatus === "PENDING"
            ? "bg-warning"
            : "bg-danger"
        }`}
      >
        {r?.trxStatus || "-"}
      </span>
    ),
  },
  {
    key: "fromAddress",
    title: "From",
    defaultVisible: true,
    render: (r) => (
      <div className="d-flex align-items-center gap-2">
        <span>{shortenAddress(r?.fromAddress)}</span>
        {r?.fromAddress && (
          <IoCopyOutline style={{ cursor: "pointer" }} onClick={() => copyToClipboard(r.fromAddress)} />
        )}
      </div>
    ),
  },
  {
    key: "toAddress",
    title: "To",
    defaultVisible: true,
    render: (r) => (
      <div className="d-flex align-items-center gap-2">
        <span>{shortenAddress(r?.toAddress)}</span>
        {r?.toAddress && (
          <IoCopyOutline style={{ cursor: "pointer" }} onClick={() => copyToClipboard(r.toAddress)} />
        )}
      </div>
    ),
  },
  {
    key: "txHash",
    title: "Tx Hash",
    defaultVisible: true,
    render: (r) => (
      <div className="d-flex align-items-center gap-2">
        <span>{shortenAddress(r?.txHash)}</span>
        {r?.txHash && (
          <IoCopyOutline style={{ cursor: "pointer" }} onClick={() => copyToClipboard(r.txHash)} />
        )}
      </div>
    ),
  },
  {
    key: "createdAt",
    title: "Date",
    defaultVisible: true,
    render: (r) => (r?.createdAt ? moment(r.createdAt).format("DD/MM/YYYY HH:mm") : "-"),
  },
];

const WalletUserDetail = () => {
  const { userId } = useParams();
  const history = useHistory();
  const [list, setList] = useState({ result: [], total: 0 });
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [type, setType] = useState("");

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "wallet-user-detail",
    allColumns: ALL_COLUMNS,
  });

  const buildQuery = (paginate) => {
    const body = {
      userId,
      pagination: paginate,
    };
    if (paginate) {
      body.page = page;
      body.sizePerPage = pageSize;
    }
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;
    if (type) body.type = type;
    return body;
  };

  const load = () => {
    setLoading(true);
    getUserWalletTrx(buildQuery(true))
      .then((res) => {
        if (res?.status) {
          const result = res?.data?.result || [];
          const total = res?.data?.total || 0;
          setList({ result, total });
          if (result.length > 0 && result[0]?.userDetails) {
            setUserInfo(result[0].userDetails);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize, startDate, endDate, type]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, startDate, endDate, type]);

  const handleDownload = (format) => {
    setDownloadLoading(true);
    getUserWalletTrx(buildQuery(false))
      .then((res) => {
        const result = res?.data?.result || [];
        if (!result.length) {
          message.warning("Nothing to download");
          return;
        }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, `wallet-${userId}`);
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleDateRangeChange = (event, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
  };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header py-3 mb-2 bg-light border-bottom">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-2">Wallet — User Transactions</h3>
                <ul className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/admin" className="text-decoration-none">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/wallet" className="text-decoration-none">Wallets</Link>
                  </li>
                  <li className="breadcrumb-item active">User Transactions</li>
                </ul>
              </div>
            </div>
          </div>

          {userInfo && (
            <div className="card mb-2">
              <div className="card-body d-flex flex-wrap align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">
                    {userInfo.firstName} {userInfo.lastName}
                  </h5>
                  <div className="text-muted">
                    {userInfo.email}
                    {userInfo.phoneNumber ? ` · ${userInfo.phoneNumber}` : ""}
                  </div>
                  {userInfo.kycStatus && (
                    <span className={`badge mt-2 ${userInfo.kycStatus === "APPROVED" ? "bg-success" : "bg-warning"}`}>
                      KYC: {userInfo.kycStatus}
                    </span>
                  )}
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => history.goBack()}>
                  Back
                </button>
              </div>
            </div>
          )}

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={list.total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                    downloadLoading={downloadLoading}
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin size="large" style={{ position: "relative", top: "35%" }} />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={dynamicColumns}
                        dataSource={list.result}
                        rowKey={(r) => r._id || r.transactionId}
                      />
                    )}
                    {list.total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={list.total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel
                count={list.total}
                onClear={() => {
                  setStartDate();
                  setEndDate();
                  setType("");
                }}
              >
                <FilterPanel.Field label="Type">
                  <select
                    className="form-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="CREDIT">Credit</option>
                    <option value="DEBIT">Debit</option>
                  </select>
                </FilterPanel.Field>
                <FilterPanel.Field label="Date Range">
                  <DateRangePicker onApply={handleDateRangeChange}>
                    <input type="text" placeholder="Select date range" readOnly />
                  </DateRangePicker>
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WalletUserDetail;
